import React, { useEffect, useState } from "react";
import { Table, Input, ConfigProvider } from "antd";

const AMBER = "#EF9F27";
const WHITE = "#FFFFFF";
const AMBER_HEADER_BG = "#FFFBEB";   // amber-50 — soft cream instead of full orange fill
const AMBER_HEADER_TEXT = "#92400E"; // amber-800 — readable dark-amber on cream
const AMBER_BORDER = "#FDE68A";      // amber-200 — light divider between header cells

// "No. of BL" arrives as a string on the query record ("4") and can be null on
// older bookings — anything that isn't a positive whole number means no rows.
function normalizeCount(v) {
  if (v === null || v === undefined) return 0;
  if (v === "") return 0;
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  if (n <= 0) return 0;
  return Math.floor(n);
}

function makeRow(key) {
  return {
    key,
    confirmationNumber: "",
    containerNumber: "",
    blNumber: "",
  };
}

function buildRows(count, previous) {
  const rows = [];
  for (let i = 1; i <= count; i++) {
    let existing = null;
    for (let j = 0; j < previous.length; j++) {
      if (previous[j].key === i) {
        existing = previous[j];
        break;
      }
    }
    rows.push(existing ? existing : makeRow(i));
  }
  return rows;
}

/**
 * Per-BL entry grid for a KAM ocean booking. Controlled: `rows` lives in the
 * parent so it can go out with the confirm payload.
 *
 * Renders nothing at all when the booking has no "No. of BL" — a booking
 * without one simply has no BLs to capture, so an empty card between the MBL
 * input and the Confirm button is noise on the confirm screen.
 *
 * @param {string|number|null} noOfBl - "No. of BL" from the query; sets the row count
 * @param {Array} rows - current rows, [{ key, confirmationNumber, containerNumber, blNumber }]
 * @param {Function} onRowsChange - receives the next rows array
 */
export default function Bl_notable({ noOfBl, rows = [], onRowsChange }) {
  const count = normalizeCount(noOfBl);

  // Row count is owned by the booking's "No. of BL", not by the user. Resync
  // when it changes (nodeData often lands after the first render), keeping
  // whatever was already typed into the rows that survive. The length guard
  // makes this a no-op once in sync, so an inline `onRowsChange` can't loop.
  useEffect(() => {
    if (rows.length === count) return;
    onRowsChange(buildRows(count, rows));
  }, [count, rows, onRowsChange]);

  // Nothing to capture (no BL count), or the rows for a fresh count haven't
  // been built by the effect above yet — either way there's no grid to show.
  // Placed after the hook so the resync still runs and clears any stale rows.
  if (count === 0 || rows.length === 0) return null;

  const handleChange = (key, dataIndex, value) => {
    const updated = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.key === key) {
        updated.push({ ...row, [dataIndex]: value });
      } else {
        updated.push(row);
      }
    }
    onRowsChange(updated);
  };

  const columns = [
    // Row label ("BL 1", "BL 2", …) so a multi-BL grid reads as a numbered
    // list. `key` is the 1-based row index, so it doubles as the BL number.
    {
      title: "BL",
      dataIndex: "key",
      key: "blLabel",
       align: "center",
      width: 80,
      render: (key) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-amber-50 border border-amber-200 text-xs font-bold text-amber-700 select-none">
          {key}
        </span>
      ),
    },
    {
      title: "Confirmation number",
      dataIndex: "confirmationNumber",
      key: "confirmationNumber",
      align: "left",
      render: (text, record) => (
        <Input
          value={text}
          placeholder="Enter confirmation number"
          style={{ width: "80%" }}
          onChange={(e) => handleChange(record.key, "confirmationNumber", e.target.value)}
        />
      ),
    },
    {
      title: "Container number",
      dataIndex: "containerNumber",
      key: "containerNumber",
      align: "left",
      render: (text, record) => (
        <Input
          value={text}
          placeholder="Enter container number"
          style={{ width: "80%" }}
          onChange={(e) => handleChange(record.key, "containerNumber", e.target.value)}
        />
      ),
    },
    {
      title: "BL number",
      dataIndex: "blNumber",
      key: "blNumber",
      align: "left",
      render: (text, record) => (
        <Input
          value={text}
          placeholder="Enter BL number"
          style={{ width: "80%" }}
          onChange={(e) => handleChange(record.key, "blNumber", e.target.value)}
        />
      ),
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* ── Section header ── */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 rounded-full bg-[#EF9F27] shrink-0" />
          <svg
            className="w-4 h-4 text-gray-400 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-sm font-bold text-gray-800 tracking-tight">
            Bill of Lading Details
          </h3>
        </div>

        {/* BL count pill */}
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
          {count} BL{count !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Table ── */}
      <div className="p-4">
        <ConfigProvider
          theme={{
            token: { colorPrimary: AMBER, borderRadiusLG: 8 },
            components: {
              Table: {
                headerBg: AMBER_HEADER_BG,
                headerColor: AMBER_HEADER_TEXT,
                headerSplitColor: AMBER_BORDER,
                borderColor: "#F3F4F6",
                borderRadius: 8,
                rowHoverBg: "#FFFBEB",
              },
              Input: {
                borderRadius: 8,
                hoverBorderColor: AMBER,
                activeBorderColor: AMBER,
                activeShadow: `0 0 0 2px rgba(239, 159, 39, 0.15)`,
              },
            },
          }}
        >
          <Table
            columns={columns}
            dataSource={rows}
            pagination={false}
            bordered
            size="middle"
            rowKey="key"
          />
        </ConfigProvider>
      </div>
    </div>
  );
}